import { Component, Input, OnInit } from '@angular/core';
import { API, mm_MediumGroup_retrieveAll_order, NamedIdentityDTO, Order, Pagination } from '../../../services/api/api';
import { BehaviorSubject } from 'rxjs';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../services/tag.service';
import { MediumGroupFullDTOAndTags } from '../../../models/taggable.model';
import { OrderOption } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'sb-booking-editor-medium-selection',
  templateUrl: './sb-booking-editor-medium-selection.component.html',
  styleUrls: ['./sb-booking-editor-medium-selection.component.scss'],
  standalone: false,
})
export class SbBookingEditorMediumSelectionComponent implements OnInit {
  @Input() selectedCompany: NamedIdentityDTO;

  mediumGroups: MediumGroupFullDTOAndTags[];

  loading = true;
  maxSize: number;
  boundaryLinks: boolean = true;

  resultCount: number;
  currentCount: number;
  collectionSize: number;
  debounceTimer: ReturnType<typeof setTimeout> = null;
  search: string;

  pagination: BehaviorSubject<Pagination>;

  paginationString: string;
  paginationInitialized: boolean;
  orderOption: OrderOption<string>;

  sizes: number[] = [5, 8, 10, 15, 20, 25];
  searchStr: string;
  exactMatch: boolean;
  hasDevices: boolean;
  ghosts = [];

  constructor(
    private api: API,
    public errorHandler: ErrorService,
    public util: UtilService,
    public translate: TranslateService,
    public tagService: TagService,
    public permissionService: PermissionService
  ) {
    this.resetOrderOption();
  }

  // tslint:disable-next-line:variable-name
  _page: number = 1;

  get page(): number {
    return this._page;
  }

  set page(val: number) {
    this._page = val;
    this.pagination.value.page = val;
  }

  ngOnInit(): void {
    this.loading = true;
    this.boundaryLinks = true;
    this.ghosts = new Array(this.maxSize); // Mock Ghost items
    this.initPagination();
  }

  initPagination() {
    this.pagination = new BehaviorSubject<Pagination>({
      page: this.page,
      count: this.maxSize ? this.maxSize : 5,
      pages: -1,
      resultcount: -1,
    });
    this.pagination.subscribe((e: Pagination) => {
      this.collectionSize = e.resultcount;
      this.maxSize = e.count;
      this.resultCount = e.resultcount;
    });

    this.requestDebounced();
  }

  requestDebounced() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    const managedCompany = this.selectedCompany ? this.selectedCompany.id : null;
    this.api
      .mm_MediumGroup_retrieveAll(
        {
          search: this.util.encodeSearchQueryParam(this.exactMatch, this.search),
          order: this.getOrder(),
          company: [managedCompany],
        },
        this.pagination
      )
      .subscribe(
        (data: MediumGroupFullDTOAndTags[]) => {
          this.loading = false;
          this.ghosts = [];
          this.mediumGroups = data;
          if (this.permissionService.hasPermission(this.permissionService.Permission.tag_list)) {
            this.mediumGroups = this.tagService.getAndAddTagEntities(this.tagService.TaggableDTO.Channel, this.mediumGroups);
          }
          this.currentCount = data.length;

          this.paginationString = this.util.getPaginationString(
            this.page,
            this.maxSize,
            this.currentCount,
            this.resultCount,
            'mediumGroups'
          );

          this.paginationInitialized = true;
        },
        error => {
          this.errorHandler.errorHandler(error);
        }
      );
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption = { value: 'Created', direction: Order.desc };
  }

  setToOrder(pValue: typeof this.orderOption.value) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
    }
    if (this.orderOption.value === pValue) {
      if (this.orderOption.direction === Order.asc) {
        this.orderOption.direction = Order.desc;
      } else {
        this.resetOrderOption();
      }
    } else {
      this.orderOption = { value: pValue, direction: Order.asc };
    }

    this.requestDebounced();
  }

  getOrder(): { by: mm_MediumGroup_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    if (!this.orderOption.value) {
      this.resetOrderOption();
      return {
        by: mm_MediumGroup_retrieveAll_order.CREATED_AT,
        direction: Order.asc,
      };
    }
    // if value equals column title
    switch (this.orderOption.value) {
      case 'Title':
        return { by: mm_MediumGroup_retrieveAll_order.NAME, direction: this.orderOption.direction };
      case 'Created':
      default:
        return { by: mm_MediumGroup_retrieveAll_order.CREATED_AT, direction: this.orderOption.direction };
    }
  }

  setMaxSize(size) {
    this.maxSize = size;
    this.pagination.value.count = size;
    this.requestDebounced();
  }

  pageChange($event) {
    this.page = $event;
    this.requestDebounced();
  }

  getFilterSelectClass() {
    if (this.selectedCompany) {
      return 'btn-hover-primary--active';
    }
    return 'btn-outline-primary';
  }

  getFilterText() {
    if (this.selectedCompany) {
      return this.selectedCompany.name;
    }
    return this.translate.instant('lang.globals.allCompanies');
  }

  doSearch($event) {
    this.search = $event;
    this.requestDebounced();
  }
}
